import {
    AttachedFiles,
    type AttachedFilesRequest,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import {
    type IHasLineItems,
    type ILineItemContainerData,
    type ILineItemData,
    type ILineItemResponse,
    LineItem,
    LineItemType,
    LinkedCostItemTypes,
    PriceTypes,
} from "legacyComponents/LineItemContainer.types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import type { AmendedPurchaseOrderLineItem } from "@btnet/services-btnet/handlers/models/amendedPurchaseOrderLineItem";
import type { AmendedPurchaseOrderRequest } from "@btnet/services-btnet/handlers/models/amendedPurchaseOrderRequest";

import {
    type AssignedUserExtraData,
    BTEntityAction,
    type BTEntityActionConfirmationExtraData,
    BTSelectItem,
    type IBaseEntity,
    type IValidator,
    type IWithServiceValidationErrors,
    type ServiceValidation,
} from "types/apiResponse/apiResponse";
import {
    type BilledStatus,
    type CostTypes,
    type CustomFieldAssociatedType,
    DocumentInstanceType,
    MarkedAs,
    type PerformingUserType,
    POApprovalStatus,
    type POWorkStatus,
    PurchaseOrderAmendmentType,
} from "types/enum";
import { type AccountingIntegrationType } from "types/enum";

import { getReadOnly } from "utilities/filters/filter";
import { calculateBuilderCost } from "utilities/lineItem/lineItemHelper";
import { isInPortal } from "utilities/portal/portal";

import { CKEditorResponse } from "commonComponents/btWrappers/BTCKEditor/BTCKEditor";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { GlobalUserSelectMapping } from "commonComponents/entity/globalUserSelector/GlobalUserSelector/GlobalUserSelector";
import {
    type IHasLinkedScheduleItem,
    LinkToScheduleItemEntity,
    type LinkToScheduleItemFormValues,
} from "commonComponents/entity/linkToScheduleItem/LinkToScheduleItem/LinkToScheduleItem.api.types";
import { MerchantPaymentListData } from "commonComponents/entity/merchantPayment/MerchantPaymentList/MerchantPaymentList";
import {
    type IRecentItemsTooltipData,
    RecentItemsTooltipConfig,
} from "commonComponents/entity/recentItemsTooltip/RecentItemsTooltip";
import { type RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type UpdateSignature } from "commonComponents/entity/signaturePad/SignaturePad/SignaturePad.api.types";
import { getVarianceForPurchaseOrders } from "commonComponents/entity/variance/variance.utils";
import {
    type IHasVariance,
    type IVarianceCodeExtraData,
    type IVarianceEntity,
    type VarianceFormValues,
} from "commonComponents/entity/variance/Variance/Variance.api.types";
import {
    type IPercentageSectionData,
    PercentageSection,
} from "commonComponents/utilities/PercentageDisplayBar/PercentageDisplayBar.types";

import { type BillDocumentType } from "entity/bill/Bill/Bill.api.types";
import {
    BillPaymentForList,
    type IBillPaymentForListData,
} from "entity/bill/Bill/BillPaymentHistory/BillPaymentHistory";
import {
    type LienWaiverStatusTypes,
    type LienWaiverType,
} from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";
import {
    getApprovalSignatureRequired,
    getPoName,
    SubPaymentListResponse,
} from "entity/purchaseOrder/common/purchaseOrders.utils";
import { type IGroupedChanges } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrderChangeSummary/ChangeSummary";
import { type IPurchaseOrderLineItem } from "entity/purchaseOrder/PurchaseOrderLineItemContainer/PurchaseOrderLineItemContainer.types";
import {
    type POPaidStatus,
    type POPaymentStatus,
} from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";
import { type TradeAgreementStatus } from "entity/sub/SubVendor/SubVendor.api.types";
import { type IExternalProductLink, type VendorTypes } from "entity/vendor/common/vendor.types";

import { type default as PurchaseorderAPIResponse } from "./PurchaseOrder.api.json";
import { type default as UnlinkChangeOrderAPIResponseExample } from "./UnlinkChangeOrder.api.json";

type UnlinkChangeOrderAPIResponse = typeof UnlinkChangeOrderAPIResponseExample;

export type PurchaseOrderFormActions =
    | undefined
    | "save"
    | "saveAndNew"
    | "saveAndClose"
    | "saveAndRelease"
    | "delete"
    | "void"
    | "updatePOStatus"
    | "updateWorkStatus"
    | "importVariance"
    | "unlinkChangeOrder"
    | "sendToTheHomeDepotCart"
    | "checkedActions"
    | "unrelease"
    | "viewStatusChangeLogs"
    | "changeLog"
    | "approve"
    | "approveWithSignature"
    | "decline";

export interface IPurchaseOrderFormValues extends IScopeOfWorkFormValues {
    customFields: ICustomFieldFormValues[];
    disclaimer: boolean;
    internalNotes: string;
    materialsOnly: boolean;
    performingUserId: number;
    performingUserName: string;
    performingUserType: PerformingUserType;
    purchaseOrderName: string;
    statusChangeComments: string;
    title: string;
    containerIsValid: boolean;
    varianceCount: number;
    lineItems: LineItem[];
    purchaseOrderLineItems: PurchaseOrderLineItem[];
    priceType: PriceTypes;
    variance: VarianceFormValues;
    unifiedDeadlineRequest?: LinkToScheduleItemFormValues;
    unlinkedBids: LinkedBid[];
    linkedBids: LinkedBid[];
    availableBids: AvailableBidToImport[];
}

export interface IScopeOfWorkFormValues {
    scopeOfWork: string;
    amendments?: string;
    attachedFiles: AttachedFilesRequest;
    attachedFilesPostApproval: AttachedFilesRequest | null;
}

export interface IPurchaseOrderCreationData {
    hasTitle: boolean;
    hasPerformingUserId: boolean;
    hasScopeOfWork: boolean;
    hasPurchaseOrdderLineItems: boolean;
}

export class PurchaseOrderEntity
    implements
        IBaseEntity,
        IHasCustomFieldContainer,
        IHasDiscussionContainer,
        IHasAttachedFiles,
        IHasLineItems,
        IHasLinkedScheduleItem,
        IHasVariance
{
    constructor(data: any) {
        this.id = data.purchaseOrderId.value;
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.instanceKey = uuidv4();
        this.approvalStatus = data.assignedStatus ? data.assignedStatus.value : null;
        this.isTemplate = data.isTemplate;

        this.canAdd = data.canAdd;
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canVoid = data.canVoid;
        this.canViewPrice = data.canViewPrice;
        this.canViewBids = data.canViewBids;
        this.canViewAccounting = data.canViewAccounting;
        this.canEditJobAccess = data.canEditJobAccess;

        this.createdBy = data.createdBy || "";
        this.createdOn = moment(data.createdOn as moment.MomentInput);
        this.createdByName = data.createdByName;
        this.createdById = data.createdById;
        this.poNamePrefix = data.purchaseOrderName.prefix;
        this.purchaseOrderName = data.purchaseOrderName.value;
        this.paidStatus = data.paidStatus ? data.paidStatus.value : null;

        this.title = data.title.value;

        this.isVoided = data.isVoided;
        this.statusChanges = data.statusChanges;

        this.amountPaidDisplay = data.totalPayments ? data.totalPayments.value : "";
        this.amountRemainingDisplay = data.amountRemaining ? data.amountRemaining.value : "";
        this.variance = getVarianceForPurchaseOrders(data as typeof PurchaseorderAPIResponse);

        this.performingUserId = data.performingUserId;

        this.lineItems = data.lineItems;

        const pendingStatuses = [
            POApprovalStatus.Unreleased,
            POApprovalStatus.PendingInternallyAssigned,
            POApprovalStatus.PendingSubReleased,
        ];

        this.lineItemContainerData = {
            pageType: "purchase-order",
            pageTypeEnum: LineItemType.PurchaseOrder,
            priceType: PriceTypes.LineItems,
            lineItemReadOnly: getReadOnly(data.lineItems.validators as IValidator[]),
            hideRadioButtons: true,
            showCatalog: !this.isVoided,
            displayTitle: true,
            supportsSavedPOPayments: true,
            canReorderLineItems: true,
            entityPending: pendingStatuses.some((s) => s === data.subApprovalStatus),
            lineItemsAmountSavedOrPaid: data.lineItemsAmountSavedOrPaid,
            amountPaidOrSaved: data.amountPaidOrSaved,
            lineItemsSentToAccounting: data.lineItemsSentToAccounting,
            lineItemsWithVoidedPOPLI: data.lineItemsWithVoidedPOPLI,
            lineItemsWithPayments: data.lineItemsWithPayments,
            canEditCostCodes: data.canEditCostCodes,
            descriptionMax: 2500,
            internalNotesMax: 2500,
            hasPriceViewingPermission: data.canViewPrice,
            entityId: data.purchaseOrderId.value,
            linkedCostItemType: LinkedCostItemTypes.PurchaseOrder,
            inactiveCostCodeCutoffDate: data.inactiveCostCodeCutoffDate,
        };

        this.lineItemInitialValues = data.lineItems.value
            ? data.lineItems.value.map(
                  (x: ILineItemResponse) =>
                      new LineItem(
                          x,
                          this.lineItemContainerData.pageType,
                          this.lineItemContainerData.pageTypeEnum,
                      ),
              )
            : [];

        this.purchaseOrderLineItems = data.lineItems?.value.map(
            (x: ILineItemResponse) => new PurchaseOrderLineItem(x) ?? [],
        );

        this.costItemUpdateMessage = data.costItemUpdateMessage;
        this.canSendToHomeDepotCart = data.canSendToHomeDepotCart ?? false;
        this.customFieldAssociatedType = data.customFieldAssociatedType;
        this.customFieldOptions = data.customFieldOptions;
        this.customFields = data.customFields;
        this.customFieldsCanConfigure = data.customFieldsCanConfigure;
        this.merchantPayments = data.merchantPayments
            ? new MerchantPaymentListData(data.merchantPayments)
            : undefined;
        this.billPayments = data.billPayments
            ? data.billPayments.map(
                  (x: any) => new BillPaymentForList(x as IBillPaymentForListData),
              )
            : [];
        this.deleteMessage = data.deleteMessage;
        this.hasPayments = data.hasPayments;
        this.hasPaymentConnectedToAccounting = data.hasPaymentConnectedToAccounting;
        this.deleteAccountingSystemMessage = data.deleteAccountingSystemMessage;
        this.voidPaymentToolTipMessage = data.voidPaymentToolTipMessage;
        this.holdPaymentMessage = data.readyForPaymentConfirmMsg;

        // Scope of Work
        this.documentInstanceType = DocumentInstanceType.PurchaseOrder;
        this.name = "Purchase Order";
        this.attachedFilesPreApproval = new PreApprovalAttachments(data);
        this.attachedFilesPostApproval = data.attachedFilesPostApproval
            ? new PostApprovalAttachments(data)
            : undefined;
        this.discussions = new DiscussionsEntity(data.discussions);
        this.scopeOfWork = new CKEditorResponse(data.scopeOfWork);
        this.amendments = data.amendments ? new CKEditorResponse(data.amendments) : null;
        this.isBill = false;
        this.accountingType = data.accountingType;
        this.lienWaiversEnabled = data.lienWaiversEnabled;
        this.totalCost = data.totalCost?.value;
        this.approvedDocInstanceId = data.approvedDocInstanceId;
        this.currentUserHasTakenApprovalAction = data.currentUserHasTakenApprovalAction;

        if (this.approvalStatus === null) {
            this.assignedStatusMessage = "Voided";
        } else {
            this.assignedStatusMessage = data.assignedStatus && data.assignedStatus.message;
        }

        this.approvalSignature = data.approvalDetails && data.approvalDetails.signature;
        this.jobName = data.jobName ? data.jobName.value : undefined;
        this.amountPaid = data.totalPayments ? data.totalPayments.value : undefined;
        this.externalId = (data as typeof PurchaseorderAPIResponse).externalId;

        // builder fields
        if (isInPortal({ builder: true })) {
            this.subApprovalStatus = new StatusDisplayConfig(
                data.statusDisplayBar.subApprovalStatus,
                data.costItemUpdateMessage as string | undefined,
            );
            this.workCompleteStatus = new StatusDisplayConfig(
                data.statusDisplayBar.workCompleteStatus,
            );

            this.recentItemsTooltip = new RecentItemsTooltipConfig(
                data.recentItemsTooltip as IRecentItemsTooltipData,
            );

            this.assignedTo = data.assignedTo.options.map((user: any) =>
                GlobalUserSelectMapping(
                    user as BTSelectItem<AssignedUserExtraData>,
                    data.assignedTo.value[0] as number | undefined,
                ),
            );

            this.linkToScheduleItemEntity = new LinkToScheduleItemEntity(data);

            this.assignedToReadOnly =
                data.assignedTo.validators &&
                getReadOnly(data.assignedTo.validators as IValidator[]);
            this.assignedToId = data.assignedTo.value[0];
            this.materialsOnly = data.materialsOnly.value;

            this.internalNotes = data.internalNotes.value;

            this.lineItemContainerData.customerVarianceCodeIds = this.variance.varianceCodes
                .filter(
                    (code: BTSelectItem<IVarianceCodeExtraData>) =>
                        code.extraData && code.extraData.isCustomerVariance,
                )
                .map((code: BTSelectItem<IVarianceCodeExtraData>) => Number(code.id));

            this.amountPaid = data.totalPayments ? data.totalPayments.value : undefined;
            this.paymentStatus = new PaymentStatus(data.statusDisplayBar.paymentStatus);
        }

        if (data.lienWaivers) {
            this.lienWaivers = data.lienWaivers.map(
                (lw: ILienWaiverLinkData) => new LienWaiverLink(lw),
            );
        } else if (data.lienWaiverList && data.lienWaiverList.length > 0) {
            this.lienWaivers = data.lienWaiverList.map(
                (lw: ILienWaiverLinkData) => new LienWaiverLink(lw),
            );
        }

        this.hasCostCodesFeature = data.hasCostCodesFeature;
        this.flatRateCostCodeId = data.flatRateCostCodeId;

        // PO amendment fields
        this.purchaseOrderAmendments = data.purchaseOrderAmendments
            ? data.purchaseOrderAmendments.map(
                  (amendment: (typeof PurchaseorderAPIResponse.purchaseOrderAmendments)[0]) =>
                      new PurchaseOrderAmendment(amendment),
              )
            : [];
        this.hasOpenAmendment = this.purchaseOrderAmendments.some(
            (amendment) => amendment.amendmentType === PurchaseOrderAmendmentType.OpenAmendment,
        );
        this.openAmendmentId = this.purchaseOrderAmendments.find(
            (amendment) => amendment.amendmentType === PurchaseOrderAmendmentType.OpenAmendment,
        )?.purchaseOrderAmendmentId;
        this.amendmentChangeSummary = data.amendmentChangeSummary;

        // sub fields`
        this.approvalSignatureRequired = getApprovalSignatureRequired(data.assignedStatus);
        this.assignedToMessage = data.assignedToMessage;
        this.canOnlyViewRfi = this.assignedToMessage !== undefined;
        this.completionDate =
            data.completionDate && data.completionDate.value
                ? moment(data.completionDate.value as moment.MomentInput)
                : undefined;
        this.dateEntered = data.dateEntered
            ? moment(data.dateEntered.value as moment.MomentInput)
            : undefined;
        this.disclaimer = data.disclaimer;
        this.infoSectionTitle = getPoName(data.purchaseOrderName || data.billId);
        this.payments = data.payments ? new SubPaymentListResponse(data.payments) : null;
        this.subId = data.subId;
        this.tradeAgreementStatus = data.tradeAgreementStatus;
        this.varianceReason = data.varianceReason;
        this.canCreateSub = data.canCreateSub;
    }

    amendments: CKEditorResponse | null;
    approvalStatus?: POApprovalStatus;
    assignedTo: BTSelectItem<AssignedUserExtraData>[];
    assignedToId: number;
    assignedToReadOnly: boolean;
    performingUserId: number;
    attachedFiles: AttachedFiles;
    attachedFilesPreApproval: PreApprovalAttachments;
    attachedFilesPostApproval?: PostApprovalAttachments;
    builderId: number;
    canAdd: boolean;
    canCreateSub: boolean;
    canDelete: boolean;
    canEdit: boolean;
    canVoid: boolean;
    canViewPrice: boolean;
    canViewBids: boolean;
    canViewAccounting: boolean;
    canEditJobAccess: boolean;
    canSendToHomeDepotCart: boolean;
    costItemUpdateMessage?: string;
    createdBy: string;
    createdOn: moment.Moment;
    createdByName: string;
    createdById: number | null;

    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldOptions: ICustomFieldOptionsList;
    customFields: ICustomFieldResponse[];
    customFieldsCanConfigure: boolean;

    discussions: DiscussionsEntity;
    documentInstanceType: DocumentInstanceType;
    id: number;
    instanceKey: string;
    internalNotes: string;
    isTemplate: boolean;
    isVoided: boolean;
    jobId: number;
    jobName: string;
    lineItemContainerData: ILineItemContainerData;
    lineItems: ILineItemData;
    lineItemInitialValues: LineItem[];
    purchaseOrderLineItems: PurchaseOrderLineItem[];
    materialsOnly: boolean;
    name: string;
    paymentStatus: PaymentStatus;
    purchaseOrderName: string;
    paidStatus: POPaidStatus;
    poNamePrefix: string;
    recentItemsTooltip: RecentItemsTooltipConfig;
    scopeOfWork: CKEditorResponse;
    statusChanges: IPurchaseOrderStatusChange[];
    subApprovalStatus: StatusDisplayConfig;
    title: string;
    workCompleteStatus: StatusDisplayConfig;
    merchantPayments: MerchantPaymentListData | undefined;
    billPayments: BillPaymentForList[];
    linkToScheduleItemEntity: LinkToScheduleItemEntity;
    amountPaidDisplay: string;
    amountRemainingDisplay: string;
    variance: IVarianceEntity;
    lienWaivers?: LienWaiverLink[];
    deleteMessage?: string;
    hasPayments?: boolean;
    hasPaymentConnectedToAccounting?: boolean;
    deleteAccountingSystemMessage?: string;
    voidPaymentToolTipMessage?: string;
    isBill: boolean;
    accountingType: AccountingIntegrationType;
    lienWaiversEnabled: boolean;
    holdPaymentMessage: string | undefined;
    totalCost: string | undefined;
    hasCostCodesFeature: boolean;
    flatRateCostCodeId?: number;
    approvedDocInstanceId?: number;
    currentUserHasTakenApprovalAction: boolean;
    externalId?: string;

    // PO amendment fields
    purchaseOrderAmendments: PurchaseOrderAmendment[];
    hasOpenAmendment: boolean;
    openAmendmentId?: number;
    amendmentChangeSummary: IGroupedChanges;

    // sub fields
    amountPaid: string;
    approvalSignature?: string | undefined;
    approvalSignatureRequired: boolean;
    assignedStatusMessage?: string | undefined;
    assignedToMessage: string;
    canOnlyViewRfi: boolean;
    completionDate: moment.Moment | undefined;
    dateEntered?: moment.Moment;
    disclaimer: string;
    infoSectionTitle: string;
    payments: SubPaymentListResponse | null;
    subId: number;
    tradeAgreementStatus: TradeAgreementStatus;
    varianceReason?: string;
}

export class StatusDisplayConfig {
    constructor(data: any, costItemUpdateMessage?: string) {
        this.title = data.title;
        this.buttonText = data.buttonText;
        this.statusTitle = data.statusText.statusTitle;
        this.statusSubTitle = data.statusText.statusSubtitle;
        this.imageHref = data.statusImages.imagePath;
        this.entityActions = data.statusActions
            ? data.statusActions.map(
                  (action: any) => new BTEntityAction<BTEntityActionConfirmationExtraData>(action),
              )
            : [];
        this.isEditable = data.isEditable;
        this.disabledAlertText = data.statusChange ? data.statusChange.alertText : null;
        this.costItemUpdateMessage = costItemUpdateMessage;
        this.isWorkComplete = data.isWorkComplete ?? false;
        this.subApprovalStatusLastUpdated = data.subApprovalStatusLastUpdated
            ? moment(data.subApprovalStatusLastUpdated as moment.MomentInput)
            : undefined;
    }

    title: string;
    buttonText: string;
    statusTitle: string;
    statusSubTitle: string;
    imageHref: string;
    entityActions: BTEntityAction<BTEntityActionConfirmationExtraData>[];
    isEditable: boolean;
    disabledAlertText: string;
    costItemUpdateMessage?: string;
    isWorkComplete: boolean;
    subApprovalStatusLastUpdated?: moment.Moment;
}

export class RelatedCO {
    constructor(data: any) {
        this.id = data.id;
        this.title = data.title;
    }

    id: number;
    title: string;
}

export class PaymentStatus {
    constructor(data: any) {
        this.amountPaidMessage = data.amountPaid;
        if (data.totalAmount === 0) {
            this.totalAmount = data.totalAmountPaid;
        } else {
            this.totalAmount = data.totalAmount;
        }
        this.totalAmountPaid = data.totalAmountPaid;
        this.message = data.message;
        this.percentageDisplay =
            data.percentageDisplayProperties?.map(
                (section: any) => new PercentageSection(section as IPercentageSectionData),
            ) ?? [];
    }

    amountPaidMessage: string;
    totalAmount: number;
    totalAmountPaid: number;
    message: string;
    percentageDisplay: PercentageSection[];
}

export class BillListResponse {
    constructor(data: any) {
        this.accountingIconSrc = data.accountingIconSrc;
        this.onlinePaymentsEnabled = data.onlinePaymentsEnabled;
        this.totalAmount = data.totalAmount?.value;
        this.totalPercentage = data.totalPercentage;
        this.canCreateBill = data.canCreateBill;
        this.bills = data.bills.map((bill: any) => new BillForList(bill));
    }

    canCreateBill: boolean;
    bills: BillForList[];
    accountingIconSrc: string | undefined;
    onlinePaymentsEnabled: boolean;
    totalAmount: number | undefined;
    totalPercentage: number;
}

export class BillForList {
    constructor(data: any) {
        this.billId = data.billId;
        this.billNumber = data.billNumber;
        this.title = data.title;
        this.dueDate = data.dueDate && moment(data.dueDate as moment.MomentInput);
        this.invoiceDate = data.invoiceDate && moment(data.invoiceDate as moment.MomentInput);
        this.paidDate = data.paidDate && moment(data.paidDate as moment.MomentInput);
        this.paidBy = data.paidByName;
        this.linkedScheduleTitle = data.linkedScheduleTitle;
        this.paymentMethodName = data.paymentMethodName;
        this.amount = data.amount?.value;
        this.percentAmount = data.percentAmount;
        this.status = data.status;
        this.documentType = (data as { documentType?: BillDocumentType }).documentType;
        this.billedStatus = data.billedStatus;
        this.hasPaidOrProcessingOnlinePayment = data.hasPaidOrProcessingOnlinePayment;
        this.isSubRequested = data.isSubRequested;
        this.isInRequestedStatus = data.isInRequestedStatus;

        this.isBilled = data.isBilled;
        this.statusText = data.statusText;
        this.isTemplateView = data.isTemplateView;
    }

    billId: number;
    billNumber: string;
    title: string;
    dueDate: moment.Moment | undefined;
    invoiceDate?: moment.Moment | undefined;
    paidDate: moment.Moment | undefined;
    paidBy: string;
    linkedScheduleTitle: string;
    paymentMethodName: string | null;
    amount: number | undefined;
    percentAmount: string;
    status: POPaymentStatus;
    documentType?: BillDocumentType;
    billedStatus: BilledStatus;
    hasPaidOrProcessingOnlinePayment: boolean;
    isSubRequested: boolean;
    isInRequestedStatus: boolean;

    isBilled: boolean;
    statusText: string;
    isTemplateView: boolean;
}

export interface ILienWaiverLinkData {
    billId?: number;
    purchaseOrderPaymentTitle?: string;
    lienWaiverTitle?: string;
    title?: string;
    lienWaiverId?: number;
    id?: number;
    lienWaiverStatus?: LienWaiverStatusTypes;
    lienWaiverDisplayStatus?: LienWaiverStatusTypes;
    status?: LienWaiverStatusTypes;
    lienWaiverType?: LienWaiverType;
}

export class LienWaiverLink {
    constructor(data: ILienWaiverLinkData) {
        this.billId = data.billId ?? 0;
        this.title = data.purchaseOrderPaymentTitle || data.lienWaiverTitle || data.title || "";
        this.lienWaiverId = data.lienWaiverId || data.id || 0;
        this.lienWaiverStatus =
            data.lienWaiverStatus ?? data.lienWaiverDisplayStatus ?? data.status ?? 0;
        this.lienWaiverType = data.lienWaiverType;
    }

    billId: number;
    title: string;
    lienWaiverId: number;
    lienWaiverStatus: LienWaiverStatusTypes;
    lienWaiverType?: LienWaiverType;
}

export interface IPurchaseOrderStatusUpdateRequest {
    currentStatus: POApprovalStatus | POWorkStatus;
    comments: string;
    updateCostItems?: boolean;
    updateSignature?: UpdateSignature;
    voidType?: PurchaseOrderVoidTypes;
    voidBillsInAccounting?: boolean;
    lineItems: LineItem[];
}

export class PurchaseOrderSaveResponse implements IWithServiceValidationErrors {
    constructor(data: any) {
        this.id = data.id;
        this.jobId = data.jobId;
        this.formMessage = data.formMessage;
        this.failedFields = data.failedFields;
    }

    id: number;
    jobId: number;
    formMessage: string;
    failedFields: ServiceValidation[];
}

export class PurchaseOrderDeleteResponse {}

export interface IPurchaseOrderStatusChange {
    previousStatus: string;
    newStatus: string;
    date: Date;
    comments: string;
    signatureSrc: string;
    approvalDocumentSrc: string;
    updatedBy: string;
}

export class LinkedBidsResponse {
    constructor(data: any) {
        this.linkedBids = data.linkedBids
            ? data.linkedBids.map((bid: LinkedBid) => new LinkedBid(bid))
            : [];
        this.canLinkBids = data.canLinkBids;
        this.availableBids = data.availableBids
            ? data.availableBids.map((bid: AvailableBidToImport) => new AvailableBidToImport(bid))
            : [];
    }
    linkedBids: LinkedBid[];
    canLinkBids: boolean;
    availableBids: AvailableBidToImport[];
}

export class LinkedBid {
    constructor(data: any) {
        this.bidId = data.bidId;
        this.bidPackageTitle = data.bidPackageTitle;
        this.dateCopied = data.dateCopied;
    }
    bidId: number;
    bidPackageTitle: string;
    dateCopied: string;
}

export class AvailableBidToImport {
    constructor(data: any) {
        this.acceptedDate = moment(data.acceptedDate as moment.MomentInput);
        this.amount = data.amount;
        this.bidId = data.bidId;
        this.bidPackageTitle = data.bidPackageTitle;
        this.fileCount = data.fileCount;
        this.subId = data.subId;
        this.subName = data.subName;
    }

    acceptedDate: moment.Moment;
    amount: string;
    bidId: number;
    bidPackageTitle: string;
    fileCount: number;
    subId: number;
    subName: string;
}

export enum PurchaseOrderLineItemColumns {
    TitleAndCostCode = 1,
    Variance = 2,
    CostTypes = 3,
    UnitCost = 4,
    Quantity = 5,
    Unit = 6,
    BuilderCost = 7,
    RelatedItem = 8,
    Delete = 9,
}

export interface IPurchaseOrderRelatedItem {
    id: number;
    title: string;
    type: number;
}

export class PurchaseOrderRelatedItem {
    constructor(data: IPurchaseOrderRelatedItem) {
        this.id = data.id;
        this.title = data.title;
        this.type = data.type;
    }

    id: number;
    title: string;
    type: RelatedItemType;
}

export interface IPurchaseOrderLineItemData extends Partial<ILineItemResponse> {
    costItemId?: number | null;
    itemTitle?: string | null;
    unit?: string | null;
    _isInEdit?: boolean;
    isExpanded?: boolean;
    autoFocusedField?: string;
    amountBilled?: number;
    amendedFromPurchaseOrderLineItemId?: number | null;
    relatedItems?: PurchaseOrderRelatedItem[] | null;
    breakLinkFromCatalog?: boolean;
}
export class PurchaseOrderLineItem implements IPurchaseOrderLineItem {
    constructor(data: IPurchaseOrderLineItemData) {
        this.quantity = Number(data.quantity);
        this.costItemId = data.costCodeItemId ?? data.costItemId ?? data.catalogItemId ?? null;
        this.unitCost = Number(data.unitCost);
        this.internalNotes = data.internalNotes ?? null;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.parentId = data.parentId ?? undefined;
        this.relatedGeneralItemId = data.relatedGeneralItemId ?? undefined;
        this.vendorProductId = data.vendorProductId ?? undefined;
        this.vendorType = data.vendorType ?? undefined;
        this.isDiscontinued = data.isDiscontinued ?? false;
        this.id = data.id ?? data.lineItemId ?? 0;
        this.costCodeId = data.costCode ?? data.costCodeId ?? null;
        this.itemTitle = data.title ?? data.itemTitle ?? null;
        this.unit = data.unitType ?? data.unit ?? "";
        this.description = data.description ?? "";
        this.isEditable = true;
        this.lineItemType = LineItemType.PurchaseOrder;
        this._isInEdit = data._isInEdit;
        this.isExpanded = data.isExpanded ?? false;
        this.builderCost = calculateBuilderCost(this.unitCost, this.quantity);
        this.varianceCodeId = data.varianceCode ?? 0;
        this.varianceCodeTitle = data.varianceCodeTitle ?? "";
        this.autoFocusedField = data.autoFocusedField ?? undefined;
        this.amountBilled = data.amountBilled ?? 0;
        this.purchaseOrderId = data.purchaseOrderId ?? 0;
        this.amendedFromPurchaseOrderLineItemId = data.amendedFromPurchaseOrderLineItemId ?? null;
        this.relatedItems = data.relatedItems
            ? data.relatedItems.map(
                  (item: unknown) =>
                      new PurchaseOrderRelatedItem(item as IPurchaseOrderRelatedItem),
              )
            : null;
        this.relatedChangeOrderLineItemId = data.relatedChangeOrderLineItemId ?? null;
    }

    quantity: number;
    costItemId: number | null;
    unitCost: number;
    builderCost: number;
    internalNotes: string | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    parentId?: number | undefined;
    relatedGeneralItemId?: number | null | undefined;
    relatedBidLineItemId: number | null | undefined;
    relatedChangeOrderLineItemId?: number | null;
    vendorProductId?: number | undefined;
    vendorType?: VendorTypes | undefined;
    isDiscontinued?: boolean | undefined;
    productLink?: IExternalProductLink | undefined;
    id: number;
    costCodeId: number | null;
    itemTitle: string | null;
    unit: string;
    description: string;
    _isInEdit?: boolean | undefined;
    isSelected?: boolean | undefined;
    isExpanded?: boolean | undefined;
    isEditable: boolean;
    autoFocusedField?: string | undefined;
    lineItemType: LineItemType;
    varianceCodeId: number;
    varianceCodeTitle: string | undefined;
    amountBilled: number;
    purchaseOrderId: number;
    amendedFromPurchaseOrderLineItemId: number | null;
    relatedItems: PurchaseOrderRelatedItem[] | null;
}

export class PreApprovalAttachments implements IHasAttachedFiles {
    constructor(data: any) {
        this.attachedFiles = new AttachedFiles(data.attachedFilesPreApproval);
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.id = data.purchaseOrderId.value;
    }
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.PurchaseOrder;
    name: string = "Pre-Approval";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number | undefined;
    builderId: number;
}

export class PostApprovalAttachments implements IHasAttachedFiles {
    constructor(data: any) {
        this.attachedFiles = new AttachedFiles(data.attachedFilesPostApproval);
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.id = data.purchaseOrderId.value;
    }
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.PurchaseOrder;
    name: string = "Post-Approval";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number | undefined;
    builderId: number;
}

export function transformPurchaseOrderLineItemsToLineItems(lineItems: PurchaseOrderLineItem[]) {
    return lineItems.map(
        (po: IPurchaseOrderLineItem) =>
            new LineItem(
                {
                    id: po.id > 0 ? po.id : 0,
                    costCodeId: po.costCodeId,
                    costCode: po.costCodeId!,
                    costCodeItemId: po.costItemId,
                    catalogItemId: po.costItemId,
                    unitCost: po.unitCost,
                    quantity: po.quantity,
                    unitType: po.unit,
                    title: po.itemTitle,
                    description: po.description,
                    costTypes: po.costTypes,
                    markedAs: po.markedAs ?? MarkedAs.None,
                    relatedGeneralItemId: po.relatedGeneralItemId,
                    relatedBidLineItemId: po.relatedBidLineItemId,
                    relatedChangeOrderLineItemId: po.relatedChangeOrderLineItemId,
                    internalNotes: po.internalNotes,
                    vendorProductId: po.vendorProductId,
                    vendorType: po.vendorType,
                    varianceCode: po.varianceCodeId,
                    builderCost: po.builderCost,
                    amendedFromPurchaseOrderLineItemId: po.amendedFromPurchaseOrderLineItemId,
                } as ILineItemResponse,
                "",
                LineItemType.PurchaseOrder,
            ),
    );
}

export function transformPurchaseOrderFormValuesToAmendedPurchaseOrderRequest(
    purchaseOrderId: number,
    jobId: number,
    values: IPurchaseOrderFormValues,
    newAndUpdatedLineItems: AmendedPurchaseOrderLineItem[],
    deletedLineItemIds: number[],
): AmendedPurchaseOrderRequest {
    return {
        ...values,
        jobId: jobId,
        amendedFromPurchaseOrderId: purchaseOrderId,
        amendedLineItems: newAndUpdatedLineItems,
        deletedLineItemIds: deletedLineItemIds,
        saveAndRelease: false,
        isVariance: values.variance.isVariance,
        hasLineItemCustomerVariance: values.variance.hasLineItemCustomerVariance,
        isEntirePoVariance: values.variance.isEntirePoVariance,
        relatedCO: values.variance.relatedCO,
        relatedPO: values.variance.relatedPOs,
        varianceCode: values.variance.varianceCode,
    } as AmendedPurchaseOrderRequest;
}

export function transformPurchaseOrderLineItemsToAmendedLineItems(
    lineItems: PurchaseOrderLineItem[],
): AmendedPurchaseOrderLineItem[] {
    return lineItems.map((poli: PurchaseOrderLineItem, index: number) => ({
        amendedFromPurchaseOrderLineItemId: poli.id > 0 ? poli.id : undefined,
        costCodeId: poli.costCodeId ?? undefined,
        costCodeItemId: poli.costItemId,
        displayOrder: index,
        quantity: poli.quantity,
        unitCost: poli.unitCost,
        unit: poli.unit,
        title: poli.itemTitle,
        description: poli.description,
        costTypes: poli.costTypes,
        markedAs: poli.markedAs ?? MarkedAs.None,
        relatedGeneralItemId: poli.relatedGeneralItemId,
        internalNotes: poli.internalNotes,
        vendorProductId: poli.vendorProductId,
        vendorType: poli.vendorType,
        varianceCode: poli.varianceCodeId,
    }));
}

export enum TabKeys {
    General = "1",
    ScopeOfWork = "2",
    StatusChangeLog = "3",
    Bids = "4",
    Payments = "5",
}

export interface IPurchaseOrderSignatureFields {
    statusChangeComments: string;
    disclaimer: boolean;
}

export enum PurchaseOrderDeleteTypes {
    DeletePurchaseOrderOnly = 0,
    DeletePurchaseOrderAndBills = 1,
}

export enum PurchaseOrderVoidTypes {
    VoidPurchaseOrderOnly = 0,
    VoidPurchaseOrderAndBills = 1,
}

export class UnlinkChangeOrderResponse {
    constructor(data: UnlinkChangeOrderAPIResponse) {
        this.relatedCOs = data.relatedCOs?.map((x: any) => new BTSelectItem(x)) ?? undefined;
    }

    relatedCOs?: BTSelectItem[];
}

export class PurchaseOrderAmendment {
    constructor(data: (typeof PurchaseorderAPIResponse.purchaseOrderAmendments)[0]) {
        this.amendmentType = data.amendmentType;
        this.approvalStatus = data.approvalStatus;
        this.purchaseOrderAmendmentId = data.purchaseOrderAmendmentId;
        this.snapshotId = data.snapshotId;
    }

    amendmentType: PurchaseOrderAmendmentType;
    approvalStatus: POApprovalStatus;
    purchaseOrderAmendmentId?: number;
    snapshotId?: string;
}
